import type { ShellSurfaceKey } from './useShellSession';

export interface SurfaceDef {
  key: ShellSurfaceKey;
  label: string;
  path: string;
  icon: string;
}

/** Base org-scoped rail surfaces, in fixed order — shown in every build. */
export const ORG_RAIL_SURFACES: ReadonlyArray<SurfaceDef> = [
  { key: 'dashboard',  label: 'Dashboard',  path: '/dashboard',  icon: '◇' },
  { key: 'projects',   label: 'Projects',   path: '/projects',   icon: '▦' },
  { key: 'documents',  label: 'Documents',  path: '/documents',  icon: '▤' },
  { key: 'work-items', label: 'Work Items', path: '/work-items', icon: '◉' },
  { key: 'members',    label: 'Members',    path: '/members',    icon: '◍' },
  { key: 'settings',   label: 'Settings',   path: '/settings',    icon: '◌' }
];

/** IDE-only Agent surface — rendered between Dashboard and Documents. */
export const AGENT_SURFACE: SurfaceDef = {
  key: 'agent', label: 'Agent', path: '/agent', icon: '◈'
};

/**
 * Org rail surfaces for the current build. Agent is now the global sidebar
 * (always visible), so the nav link is no longer included in any build.
 */
export function getOrgRailSurfaces(): ReadonlyArray<SurfaceDef> {
  return ORG_RAIL_SURFACES;
}

/** User-scoped rail surfaces — rendered below org items with a divider. */
export const PERSONAL_RAIL_SURFACES: ReadonlyArray<SurfaceDef> = [
  { key: 'todos', label: 'My todos', path: '/personal-todos', icon: '☐' }
];

/**
 * Every known rail surface across all builds — used for icon/badge typing
 * and path->key lookup. The actually-rendered org list comes from
 * `getOrgRailSurfaces()`.
 */
export const RAIL_SURFACES: ReadonlyArray<SurfaceDef> = [
  ORG_RAIL_SURFACES[0],
  AGENT_SURFACE,
  ...ORG_RAIL_SURFACES.slice(1),
  ...PERSONAL_RAIL_SURFACES
];

export function isRailSurface(path: string): ShellSurfaceKey | null {
  const match = RAIL_SURFACES.find((s) => s.path === path);
  return match?.key ?? null;
}

/**
 * Whether `pathname` is an item-editor route — one that has a real item OPEN
 * (Document / Work Item / Project), as opposed to a list, the dashboard, the
 * standalone agent panel, or settings. These are exactly the routes whose pages
 * call `useTrackOpenItem` to record the open item.
 *
 * The agent's active target (the "Acting on" chip + the per-turn context
 * overlay) must reflect what is CURRENTLY open: the shell clears the open-item
 * store whenever the route is NOT one of these, so a previously-opened item
 * never lingers after navigating away or switching orgs. The trailing segment
 * must be a non-empty id, so the list routes (`/documents`, `/projects`) and the
 * standalone agent panel (`/agent`) are correctly excluded.
 */
const ITEM_EDITOR_ROUTE_RES: ReadonlyArray<RegExp> = [
  /^\/documents\/[^/]+/,
  /^\/document-editor\/[^/]+/,
  /^\/work-item-detail\/[^/]+/,
  /^\/projects\/[^/]+/
];

export function isItemEditorRoute(pathname: string): boolean {
  return ITEM_EDITOR_ROUTE_RES.some((re) => re.test(pathname));
}
